import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import StatusBadge from "../../components/common/StatusBadge";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../components/common/Pagination";
import SearchBar from "../../components/common/Search";
import CustomFilterDropdown from "../../components/common/CustomFilterDropdown";
import ActionMenu from "../../components/common/ActionMenu";

import {
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineEye,
} from "react-icons/ai";

import {
  useAdminExperiences,
  useAdminExperienceApprove,
  useAdminExperienceReject,
  useAdminVendorsDropdown,
} from "../../custom-hooks/queries";

import { formatDate } from "../../utils/HelperMethod";
import RejectionModal from "../../components/Modals/RejectionModal";
import { toast } from "react-toastify";

// 🔹 Filter options
const statusOptions = [
  { id: "Pending", name: "Pending" },
  { id: "Approved", name: "Approved" },
  { id: "Rejected", name: "Rejected" },
  { id: "Suspended", name: "Suspended" },
];

// 🔹 Vendor dropdown (mock for now)
const vendorOptions = [
  { id: "vendor1", name: "Adventure Co" },
  { id: "vendor2", name: "Desert Fun" },
];

export default function ExperienceManagement() {
  const navigate = useNavigate();

  // 🔹 Table & filters state
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState(null);
  const [vendorFilter, setVendorFilter] = useState(null);
  const [searchText, setSearchText] = useState("");
  const pageSize = 10;

  // 🔹 Reject modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);

  // 🔹 Queries & mutations
  const { data, isLoading, isError, refetch } = useAdminExperiences({
    currentPage,
    pageSize,
    search: searchText,
    vendorId: vendorFilter,
    status: statusFilter,
  });

  const approveMutation = useAdminExperienceApprove();
  const rejectMutation = useAdminExperienceReject();

  const { data: vendorDropdown } = useAdminVendorsDropdown();

  const vendorOptions =
    vendorDropdown?.data?.map((v) => ({
      id: v.vendorId,
      name: v.buisnessName
        ? `${v.buisnessName} (${v.vendorName?.trim() || "Vendor"})`
        : v.vendorName?.trim() || "Vendor",
    })) || [];

  // 🔹 Map API → table rows
  const experiences =
    data?.data?.data?.map((item) => ({
      experienceId: item.experienceId,
      title: item.title,
      vendorName: item.vendorName,
      category: item.category,
      price: item.price,
      status: item.status,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      totalBookings: item.totalBookings,
    })) || [];

  // 🔹 Action menu
  const experienceActions = (row) => [
    {
      label: "View Experience",
      icon: <AiOutlineEye />,
      onClick: () => navigate(`/admin/experiences/${row.experienceId}`),
    },

    ...(row?.status?.toLowerCase() !== "approved"
      ? [
          {
            label: "Approve Experience",
            icon: <AiOutlineCheckCircle />,
            onClick: () =>
              approveMutation.mutate(row.experienceId, {
                onSuccess: () => {
                  toast.success("Experience approved successfully!");
                  refetch();
                },
                onError: (error) => {
                  toast.error(
                    error.response?.data?.message ||
                      "Failed to approve experience. Please try again.",
                  );
                },
              }),
          },
        ]
      : []),

    {
      label: "Reject Experience",
      icon: <AiOutlineCloseCircle />,
      danger: true,
      onClick: () => {
        setSelectedExperience(row);
        setShowRejectModal(true);
      },
    },
  ];

  // 🔹 Columns
  const columns = [
    {
      key: "title",
      label: "TITLE",
      width: "1.6fr",
      align: "left",
      render: (row) => (
        <div className="font-semibold text-gray-900 break-words whitespace-normal max-w-full">
          {row.title}
        </div>
      ),
    },
    {
      key: "vendorName",
      label: "VENDOR",
      width: "1fr",
      align: "left",
      render: (row) => (
        <div className="text-gray-700 break-words whitespace-normal max-w-full">
          {row.vendorName}
        </div>
      ),
    },
    {
      key: "category",
      label: "CATEGORY",
      width: "1.2fr",
      align: "left",
    },
    {
      key: "price",
      label: "PRICE",
      width: "1fr",
      align: "center",
      render: (row) => `$${row.price}`,
    },
    {
      key: "status",
      label: "STATUS",
      width: "1.2fr",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "actions",
      label: "ACTIONS",
      width: "0.8fr",
      align: "center",
      render: (row) => <ActionMenu actions={experienceActions(row)} />,
    },
  ];

  // 🔹 Handlers
  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (statusId) => {
    setStatusFilter(statusId);
    setCurrentPage(1);
  };

  const handleVendorChange = (vendorId) => {
    setVendorFilter(vendorId);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="px-4 py-1 sm:px-6 sm:py-1">
      <PageHeader
        title="Experience Management"
        description="Discover and manage experiences"
      />

      <div className="bg-white rounded-[12px] border border-[#D9D9D9] shadow-sm overflow-hidden mt-4 sm:mt-6">
        {/* 🔍 Search + Filters */}
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 py-6 sm:py-10 px-4 sm:px-6">
          <SearchBar onSearch={handleSearch} />

          <div className="flex flex-col sm:flex-row gap-3">
            <CustomFilterDropdown
              options={statusOptions}
              onChange={handleStatusChange}
              placeholder="Filter by Status"
              valueKey="id"
              labelKey="name"
            />

            <CustomFilterDropdown
              options={vendorOptions}
              onChange={handleVendorChange}
              placeholder="Filter by Vendor"
              valueKey="id"
              labelKey="name"
            />
          </div>
        </div>

        {/* 📋 Table */}
        {isError ? (
          <div className="flex justify-center items-center py-20 text-red-500">
            Failed to load experiences.
          </div>
        ) : (
          <Table
            columns={columns}
            data={experiences}
            isLoading={isLoading}
            skeletonRows={pageSize}
          />
        )}
      </div>

      {/* 📄 Pagination */}
      {!isLoading && !isError && data?.data?.totalPages > 1 && (
        <div className="mt-4 sm:mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={data?.data?.totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* ❌ Reject Modal */}
      {showRejectModal && selectedExperience && (
        <RejectionModal
          title="Reject Experience"
          description="Please provide a reason for rejecting this experience."
          confirmText="Reject Experience"
          mutationFn={rejectMutation}
          payload={{
            experienceId: selectedExperience.experienceId,
          }}
          refetch={refetch}
          onClose={() => {
            setShowRejectModal(false);
            setSelectedExperience(null);
          }}
          actionType="reject"
        />
      )}
    </div>
  );
}
